/*
 * Pure helpers behind the client header: display strings, expiration urgency
 * and the trailing section crumb. No React, no IO, so they are unit-tested
 * directly.
 */
import type { Address } from "../../../api/types/foreignNational";
import { NAV_ITEMS } from "../navConfig";
import { type ClientProfileUrlState, type ClientTab } from "../urlState";

import { calendarDay } from "../../caseIde/header/caseHeaderModel";

export {
  calendarDay,
  displayName,
  initialsOf
} from "../../caseIde/header/caseHeaderModel";

export type ExpirationUrgency = "none" | "ok" | "soon" | "expired";

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Whole days from `now` to the date (negative when past), or null if invalid. */
export const daysUntil = (
  iso: string,
  now: Date = new Date()
): number | null => {
  const d = calendarDay(iso);
  if (!d) return null;
  return Math.round((startOfDay(d) - startOfDay(now)) / DAY_MS);
};

/** "soon" inside 90 days (status expirations are slow-moving), "expired" once past. */
export const expirationUrgency = (
  iso?: string | null,
  now: Date = new Date()
): ExpirationUrgency => {
  if (!iso) return "none";
  const days = daysUntil(iso, now);
  if (days === null) return "none";
  if (days < 0) return "expired";
  if (days <= 90) return "soon";
  return "ok";
};

export const formatShortDate = (iso?: string | null): string => {
  const d = calendarDay(iso);
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/** Single-line postal address, or "" when empty. */
export const formatAddressLine = (address?: Address | null): string => {
  if (!address) return "";
  const street = (address.street ?? "").toString().trim();
  const city = (address.city ?? "").toString().trim();
  const state = (address.state ?? "").toString().trim();
  const zip = (address.zip_code ?? address.zipCode ?? address.zipcode ?? "")
    .toString()
    .trim();
  const country = (address.country ?? "").toString().trim();
  const cityStateZip = [city, state].filter(Boolean).join(", ");
  return [street, [cityStateZip, zip].filter(Boolean).join(" "), country]
    .filter(Boolean)
    .join(", ");
};

export type SectionCrumb = {
  tab: ClientTab;
  label: string;
  /** Extra trailing label (e.g. the intake flow) shown after the section. */
  detail: string | null;
};

/**
 * The crumb after the client name. Null on the overview (the pill is the
 * client itself there); on Requests the intake flow adds a detail segment.
 */
export const sectionCrumbFor = (
  state: Pick<ClientProfileUrlState, "tab">,
  /** The Requests IDE's open tab title, when that tab is active. */
  requestsTitle?: string | null
): SectionCrumb | null => {
  if (state.tab === "overview") return null;
  const detail =
    state.tab === "outgoing_intakes" ? requestsTitle?.trim() || null : null;
  return { tab: state.tab, label: NAV_ITEMS[state.tab].label, detail };
};
